<template>
  <div>
    <page-header :title="title" :breadcrumb="breadcrumb" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <page-separator :title="title" />

        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Filters -->
              <div class="col-lg filters-row d-flex flex-wrap align-items-center">
                <div class="filter-item mb-2">
                  <form class="search-form search-form--light d-lg-inline-flex" @submit.prevent="">
                    <b-form-input
                      class="w-lg-auto"
                      placeholder="Search by name, email, phone..."
                      v-model="searchQuery"
                      @input="onFiltersChange"
                    />
                    <b-btn variant="flush" type="submit">
                      <md-icon>search</md-icon>
                    </b-btn>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <!-- Referred Students Table -->
          <b-table
            :fields="tableColumns"
            :items="students"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
          >
            <template #cell(student)="data">
              {{ getStudentName(data.item) }}
            </template>
            <template #cell(email)="data">
              {{ getStudentEmail(data.item) }}
            </template>
            <template #cell(phone)="data">
              {{ getStudentPhone(data.item) }}
            </template>
            <template #cell(program)="data">
              {{ getProgramName(data.item) }}
            </template>
            <template #cell(referrer)="data">
              {{ getReferrerName(data.item) }}
            </template>
            <template #cell(enrollment_status)="data">
              {{ getEnrollmentStatus(data.item) }}
            </template>
            <template #cell(status)="data">
              {{ getStudentStatus(data.item) }}
            </template>
            <template #cell(created_at)="data">
              {{ getReferredAt(data.item) ? formatDateTime(getReferredAt(data.item)) : '' }}
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalStudents"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="referred-students-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { debounce, get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';

import { DEFAULT_PAGE_SIZE, USER_ROLES } from '../../../common/constants';
import { formatDateTime } from '../../../common/utils';

export default {
  components: { PageHeader, PageSeparator, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Referred Students',

      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalStudents: 0,
      students: [],

      selectedSchoolId: null,
      selectedProgramId: null,
      searchQuery: '',
      enrollmentStatus: null,
      studentStatus: null,

      allSchools: [],
      allPrograms: [],
      areSchoolsLoading: false,
      areProgramsLoading: false,

      ordering: null,
      USER_ROLES,

      enrollmentStatusOptions: [
        { value: null, text: 'All Enrollment Status' },
        { value: 'enrolled', text: 'Enrolled' },
        { value: 'pending', text: 'Pending' },
        { value: 'cancelled', text: 'Cancelled' },
      ],
      studentStatusOptions: [
        { value: null, text: 'All Status' },
        { value: 'active', text: 'Active' },
        { value: 'inactive', text: 'Inactive' },
      ],
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    isSuperAdmin() {
      return this.getLoggedInUser.role === USER_ROLES.SUPER_ADMIN;
    },

    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Referred Students', active: true },
      ];
    },

    tableColumns() {
      const cols = [
        { key: 'student', label: 'Student' },
        { key: 'email', label: this.$t('authMsgs.email') },
        { key: 'phone', label: this.$t('studentMsgs.phone') },
        { key: 'program', label: 'Program' },
        ...(this.isSuperAdmin ? [{ key: 'referrer', label: 'Referred By' }] : []),
        { key: 'school_name', label: "Program's School" },
        { key: 'created_at', label: 'Registration Date' },
      ];

      return cols;
    },
  },

  watch: {
    selectedSchoolId() {
      if (this.isSuperAdmin) {
        this.selectedProgramId = null;
        if (this.selectedSchoolId) {
          this.fetchPrograms();
        } else {
          this.allPrograms = [];
        }
        this.fetchReferredStudents();
      }
    },
  },

  methods: {
    ...mapActions('student', ['getReferredStudents']),
    ...mapActions('school', ['getAllSchools']),
    ...mapActions('program', ['getAllPrograms', 'getSclPrograms']),
    formatDateTime,
    get,

    getListResults(response) {
      const data = get(response, 'data', response);
      if (Array.isArray(data)) {
        return data;
      }
      if (Array.isArray(get(data, 'results'))) {
        return data.results;
      }
      return [];
    },

    getUserName(entity) {
      const user = get(entity, 'user', entity) || {};
      const name = [get(user, 'first_name'), get(user, 'last_name')]
        .filter(Boolean)
        .join(' ')
        .trim();
      return name || get(user, 'name', '');
    },

    getStudentName(item) {
      return get(item, 'student_name', '');
    },

    getStudentEmail(item) {
      return get(item, 'student_email', '');
    },

    getStudentPhone(item) {
      return get(item, 'student_phone', '');
    },

    getProgramName(item) {
      return get(item, 'program_title', '');
    },

    getReferrerName(item) {
      return get(item, 'referral_info.entity_name', '') || get(item, 'referral_info.referred_by', '');
    },

    getReferredAt(item) {
      return get(item, 'registration_date', null);
    },

    getEnrollmentStatus(item) {
      const status = get(item, 'enrollment_status', '');
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
    },

    getStudentStatus(item) {
      const status = get(item, 'status', '');
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
    },

    onFiltersChange() {
      this.debouncedFetchReferredStudents(this);
    },

    async fetchSchools() {
      this.areSchoolsLoading = true;
      try {
        const response = await this.getAllSchools();
        this.allSchools = this.getListResults(response);
      } catch (error) {
        this.allSchools = [];
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areSchoolsLoading = false;
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;
      try {
        let response;
        if (this.isSuperAdmin) {
          if (!this.selectedSchoolId) {
            this.allPrograms = [];
            this.areProgramsLoading = false;
            return;
          }
          response = await this.getAllPrograms({ school: this.selectedSchoolId, ordering: 'id' });
        } else {
          response = await this.getSclPrograms();
        }
        this.allPrograms = this.getListResults(response);
      } catch (error) {
        this.allPrograms = [];
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.areProgramsLoading = false;
    },

    async fetchReferredStudents(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      try {
        const response = await this.getReferredStudents({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...(this.selectedSchoolId && { school_id: this.selectedSchoolId }),
          ...(this.searchQuery && { search: this.searchQuery }),
          ...(this.selectedProgramId && { program_id: this.selectedProgramId }),
          ...(this.enrollmentStatus && { enrollment_status: this.enrollmentStatus }),
          ...(this.studentStatus && { status: this.studentStatus }),
          ...params,
        });

        this.students = get(response, 'data.results', []);
        this.currentPage = pageNum;
        this.totalStudents = get(response, 'data.count', this.students.length);
      } catch (error) {
        this.students = [];
        this.totalStudents = 0;
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchReferredStudents(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchReferredStudents();
    },

    debouncedFetchReferredStudents: debounce(vm => {
      vm.fetchReferredStudents();
    }, 500),
  },

  async mounted() {
    if (this.isSuperAdmin) {
      await this.fetchSchools();
    } else {
      await this.fetchPrograms();
    }
    this.fetchReferredStudents();
  },
};
</script>

<style scoped>
.filters-row {
  gap: 12px;
}

.filter-item {
  flex: 0 0 260px;
  max-width: 320px;
}

@media (max-width: 991.98px) {
  .filters-row {
    justify-content: flex-start;
  }

  .filter-item {
    flex: 1 1 100%;
    max-width: 100%;
  }
}
</style>
